import type { AfgorelseOverviewModel } from "@/interfaces/afgorelse";
import { PaabudStatus } from "@/interfaces/paabud";

export type AfgorelseStatusPresentation = {
  className: string;
  label: string;
};

/** Vises hvor en dato mangler, fordi forløbet står stille. */
export const I_BERO_LABEL = "I bero";

/** Vises når afgørelsen er lukket uden indsendelse — henlagt, fordi advokaten er død. */
export const AFSLUTTET_LABEL = "Afsluttet";

/** Vises som dokumentationsperiode, når forløbet er i bero og perioden derfor står stille. */
export const PERIODE_LOEBER_IKKE_LABEL = "Løber ikke endnu";

/**
 * Vises når afgørelsen ligger hos Advokatsamfundet frem for hos advokaten.
 *
 * Tilstanden opstår, når AIKA har flyttet indberetningen væk fra "frist ordinær" uden at
 * der er indsendt noget — undersøges, indbragt for Advokatnævnet eller henlagt. Fælles
 * for dem er, at sagen behandles, og at advokaten ikke skal foretage sig noget.
 * Badgen hed før "Ingen åben indberetning", hvilket kun beskrev systemtilstanden
 * (harAabenIndberetning === false) og ikke, hvad den betød for medlemmet.
 */
export const UNDER_BEHANDLING_LABEL = "Under behandling";

export const formatDanishDate = (
  date: string | Date | null | undefined,
): string => {
  if (!date) {
    return "-";
  }

  return new Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(date));
};

/**
 * Statussen på en afgørelse, som den vises i tabellen.
 *
 * Rækkefølgen er ikke tilfældig: indsendt slår alt, så en afsluttet (henlagt) afgørelse,
 * dernæst en åben indberetning, og først derefter bero. En sag i bero kendes på, at der ikke er nogen frist — fristen
 * løber kun, mens advokaten er udøvende, så en deponeret beskikkelse rydder den.
 *
 * Til sidst står "under behandling": der er en frist, sagen er ikke afsluttet, og der er
 * alligevel ingen kladde at udfylde. Så ligger den hos Advokatsamfundet.
 */
export const getAfgorelseStatusPresentation = (
  afgorelse: Pick<
    AfgorelseOverviewModel,
    "indsendt" | "harAabenIndberetning" | "frist" | "status"
  >,
): AfgorelseStatusPresentation => {
  if (afgorelse.indsendt) {
    return {
      className: "cle-status-badge--success",
      label: "Indsendt",
    };
  }

  // EFTER indsendt, aldrig før: en opfyldt afgørelse er også Lukket og skal vise "Indsendt".
  // Her er den lukket UDEN indsendelse — henlagt, fordi advokaten er død.
  if (afgorelse.status === PaabudStatus.Lukket) {
    return {
      className: "cle-status-badge--info__soft",
      label: AFSLUTTET_LABEL,
    };
  }

  if (afgorelse.harAabenIndberetning) {
    return {
      className: "cle-status-badge--warning",
      label: "Afventer indberetning",
    };
  }

  if (!afgorelse.frist) {
    return {
      className: "cle-status-badge--info__soft",
      label: I_BERO_LABEL,
    };
  }

  return {
    className: "cle-status-badge--info__soft",
    label: UNDER_BEHANDLING_LABEL,
  };
};

/**
 * Dokumentationsperioden til visning. En tom slutdato skal forklares frem for at stå
 * som en bindestreg: perioden er ikke udløbet, den er ikke ukendt.
 */
export const formaterDokumentationsperiode = (
  periodeFra: string,
  periodeTilMinusEnDag: string | null,
): string =>
  periodeTilMinusEnDag
    ? `${formatDanishDate(periodeFra)} - ${formatDanishDate(periodeTilMinusEnDag)}`
    : PERIODE_LOEBER_IKKE_LABEL;

/** Indberetningsfristen til visning. Ingen frist betyder, at forløbet står i bero. */
export const formaterIndberetningsfrist = (frist: string | null): string =>
  frist ? formatDanishDate(frist) : I_BERO_LABEL;
